import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiContenteditableComponent } from '@fi-shared/fi-contenteditable/fi-contenteditable.component';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { FiInsertMedia2Service } from '@fi-shared/fi-insert-media/fi-insert-media2.service';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiLinkToType } from '@fi-shared/fi-text-item-helper/fi-link-to-type';
import { ITextEditorLinkAttachment, ITextEditorActiveButtons } from '@fi-shared/fi-text-editor/fi-text-editor';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { TextEditorInsertEmbedVideoService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-embed-video/text-editor-insert-embed-video.service';
import { TextEditorInsertLinkService } from '@fi-shared/fi-text-editor/fi-text-editor-insert-link/text-editor-insert-link.service';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';
import { eventFindInPath } from '@fi-shared/utils';
import { AppTextService } from '@fi-shared/app-text';
import { FiAttachmentsListComponent } from '@fi-shared/media-bank/fi-attachments/fi-attachments-list.component';
import { FiFileUpload2Service } from '@fi-shared/fi-insert-media/fi-file-upload2.service';
import { FiInsertMediaUploadHelperComponent } from '@fi-shared/fi-insert-media/fi-insert-media-upload-helper/fi-insert-media-upload-helper.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { takeUntil } from 'rxjs/operators';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { ObjectTypes } from '@fi-shared/types';
import { FiReplaceLinkTypes } from '@fi-shared/fi-directives/replace-with-links/replace-with-links.directive';
import { MatFormFieldControlBase } from '@fi-shared/widget/mat-form-field-control-base';
import { MatFormFieldControl } from '@angular/material/form-field';
import { FiDndOverlayComponent } from '@fi-shared/fi-insert-media/fi-dnd-overlay/fi-dnd-overlay.component';
import { ILightWeightUser } from '@fi-shared/models';
import { Animations } from '@fi-app/shared/utils/animations';

@Component({
    selector: 'fi-text-editor',
    template: `
        <div class="fi-text-editor-wrapper">
            <fi-dnd-overlay
                #fiDndOverlayComponent
                [parentElementRef]="elementRef"
                (uploadFiles)="onFilesSelected($event)"
            />
            <fi-contenteditable
                class="fi-standard-content-editable-text-editor"
                [isMaterialDesign]="true"
                [(ngModel)]="ngModel"
                (ngModelChange)="onNgModelChange()"
                [placeholder]="placeholder"
                [autofocus]="autofocus"
                [required]="required"
                [disableFormattingOnPaste]="disableFormattingOnPaste"
                [taggedUsers]="taggedUsers"
                (click)="onContenteditableClick($event)"
                (focus)="onContenteditableFocus($event)"
                (blur)="onContenteditableBlur($event)"
                (filesPasted)="onFilesPasted($event)"
                [ngClass]="{ expanded: (isDraggingFile() | async) }"
                [replaceLinkTypes]="[FiReplaceLinkTypes.Url, FiReplaceLinkTypes.Email]"
                #fiContenteditable
            />
        </div>

        <fi-insert-media-upload-helper (onFilesSelect)="onFilesSelected($event)" />

        <div class="fi-text-editor-actions" [hidden]="!(showActionsByFocus$ | async) && !alwaysShowActions">
            @if (displayEditorActions) {
                <fi-text-editor-panel
                    @fadeHeight
                    [inputElementRef]="fiContenteditable.contenteditableElRef"
                    [textEditorOpenedDialogs$]="textEditorOpenedDialogs$.asObservable()"
                    (documentCommandExecute)="fiContenteditable.triggerChangeEvent()"
                    (insertMediaUsingFileManager)="openInsertMediaDialog()"
                    (insertMediaUsingCamera)="insertMediaUsingCamera()"
                    (insertMediaUsingBrowseDevice)="insertMediaUsingBrowseDevice()"
                    (insertLink)="openInsertLinkDialog($event)"
                    (insertEmbedVideo)="openInsertEmbedVideoDialog()"
                    (insertLinkAttachment)="onInsertLinkAttachment($event)"
                />
            }

            <fi-text-editor-tag-users2
                [editorElementRef]="fiContenteditable.contenteditableElRef"
                (contentChange)="fiContenteditable.triggerChangeEvent()"
                (newUserTagged)="onNewUserTagged($event)"
            />
            @if (displayEditorActions) {
                <div class="fi-text-editor-action-buttons" @fadeHeight>
                    <button mat-stroked-button class="BrandedBorderColor BrandedColor" (click)="onCancelClick($event)">
                        {{ 'common.cancel' | fiAppText }}
                    </button>
                    <button
                        mat-flat-button
                        class="BrandedBackgroundColor"
                        [disabled]="isSaveDisabled()"
                        (click)="onSaveClick($event)"
                    >
                        {{ saveButtonText }}
                    </button>
                </div>
            }
        </div>

        @if (isAttachmentListShown) {
            <fi-attachments-list
                [mediaBankFileReferences]="mediaBankFileReferences"
                [linkAttachments]="linkAttachments"
                [editMode]="true"
                (mediaBankFileReferencesChange)="onMediaBankFileReferencesChanged($event)"
                (linkAttachmentsChange)="onLinkAttachmentsChanged($event)"
                (editLinkAttachment)="onEditLinkAttachment($event)"
            />
        }
    `,
    styleUrls: ['./fi-text-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-text-editor' },
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: FiTextEditorComponent,
        },
    ],
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiTextEditorComponent extends MatFormFieldControlBase<string> implements OnInit, OnDestroy {
    @Input() mediaBankFileReferences: IMediaBankFileReference[] = [];
    @Input() mediaBankFileContentLanguageId: number;
    @Input() linkAttachments: ITextEditorLinkAttachment[] = [];
    @Input() saveButtonText: string;
    @Input() autofocus: boolean;
    @Input() alwaysShowActions: boolean = false;
    @Input() saveDisabled = false;
    @Input() objectType: ObjectTypes;
    @Input() disableFormattingOnPaste: boolean;
    @Input() isAttachmentListShown: boolean = true;
    @Input() taggedUsers: ILightWeightUser[] = [];
    @Input() displayEditorActions: boolean = true;

    @Output() mediaBankFileReferencesChange = new EventEmitter<IMediaBankFileReference[]>();
    @Output() linkAttachmentsChange = new EventEmitter<ITextEditorLinkAttachment[]>();
    @Output() saveClick = new EventEmitter<MouseEvent>();
    @Output() cancelClick = new EventEmitter<MouseEvent>();
    @Output() focus = new EventEmitter<FocusEvent>();
    @Output() blur = new EventEmitter<FocusEvent>();

    @ViewChild(FiContenteditableComponent, { static: true }) contenteditableComponent: FiContenteditableComponent;
    @ViewChild(FiAttachmentsListComponent, { static: false }) attachmentsListComponent: FiAttachmentsListComponent;
    @ViewChild(FiInsertMediaUploadHelperComponent, { static: true })
    uploadHelperComponent: FiInsertMediaUploadHelperComponent;
    @ViewChild('fiDndOverlayComponent') fiDndOverlayComponent: FiDndOverlayComponent;

    showActionsByFocus$ = new ReplaySubject<boolean>(1);
    focusedNodeElement: HTMLElement;
    textEditorOpenedDialogs$ = new BehaviorSubject<ITextEditorActiveButtons>({});

    FiReplaceLinkTypes = FiReplaceLinkTypes;

    public get editorElement(): ElementRef<HTMLElement> {
        return this.contenteditableComponent.contenteditableElRef;
    }
    constructor(
        injector: Injector,
        private insertMedia2Service: FiInsertMedia2Service,
        private textEditorInsertEmbedVideoService: TextEditorInsertEmbedVideoService,
        private textEditorInsertLinkService: TextEditorInsertLinkService,
        public elementRef: ElementRef<HTMLElement>,
        private advancedTextHandlerService: AdvancedTextHandlerService,
        private appTextService: AppTextService,
        private fileUpload2Service: FiFileUpload2Service,
        private fiDialogsService: FiDialogsService,
        private fiBrowserEventsService: FiBrowserEventsService,
        private textEditorService: TextEditorService,
    ) {
        super(injector);
    }

    ngOnInit() {
        if (!this.saveButtonText) this.saveButtonText = this.appTextService.get('common.save');

        this.fiBrowserEventsService
            .getWindowEventSubject$('keydown')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => {
                this.textEditorService.handleTabKeyPress($event);
            });
    }

    isDraggingFile(): Observable<boolean> {
        return this.fiDndOverlayComponent?.fiDndHelper.dragging$;
    }

    onNgModelChange() {
        this.doPropagateChanges();
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
    }

    onContenteditableFocus($event: FocusEvent) {
        this.showActionsByFocus$.next(true);
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
        this.focus.emit($event);
    }

    onContenteditableBlur($event: FocusEvent) {
        this.blur.emit($event);
    }

    isSaveDisabled() {
        return !!this.validationErrors || this.saveDisabled;
    }

    onSaveClick($event: MouseEvent) {
        this.showActionsByFocus$.next(false);
        this.saveClick.next($event);
    }

    onCancelClick($event: MouseEvent) {
        if (this.isDirty) {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('common.discard_draft_dialog_text'),
                    yesButtonText: this.appTextService.get('common.discard'),
                })
                .onYesButtonClick(() => {
                    this.cancelClickInternal();
                });
        } else {
            this.cancelClickInternal();
        }
    }
    private cancelClickInternal() {
        this.ngModel = undefined;
        this.onMediaBankFileReferencesChanged([]);
        this.showActionsByFocus$.next(false);
        this.cancelClick.emit();
    }

    private setTextEditorOpenedDialogsData(prop: string, value: boolean) {
        this.textEditorOpenedDialogs$.next({ ...this.textEditorOpenedDialogs$.value, [prop]: value });
    }

    openInsertMediaDialog(filesToUpload?: ArrayLike<File>) {
        this.setTextEditorOpenedDialogsData('attachments', true);
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(this.objectType, {
                filesToUpload: filesToUpload,
                contentLanguageId: this.mediaBankFileContentLanguageId,
            })
            .onYesButtonClick((result) => {
                this.onMediaBankFileReferencesChanged(
                    this.mediaBankFileReferences.concat(result.mediaBankFileReferences),
                );
            })
            .afterClosed(() => {
                this.setTextEditorOpenedDialogsData('attachments', false);
            });
    }

    onMediaBankFileReferencesChanged($event: IMediaBankFileReference[]) {
        this.mediaBankFileReferences = $event;
        this.mediaBankFileReferencesChange.emit($event);
        this.doPropagateChanges();
    }

    onLinkAttachmentsChanged($event: ITextEditorLinkAttachment[]) {
        this.linkAttachments = $event;
        this.linkAttachmentsChange.emit($event);
        this.doPropagateChanges();
    }

    openInsertLinkDialog($event: FiLinkToType) {
        this.setTextEditorOpenedDialogsData('link', true);
        this.textEditorInsertLinkService
            .insertLink({
                parentElement: this.elementRef.nativeElement,
                linkToType: $event,
            })
            .onYesButtonClick(() => this.contenteditableComponent.triggerChangeEvent())
            .afterClosed(() => this.setTextEditorOpenedDialogsData('link', false));
    }

    openInsertEmbedVideoDialog(attachment?: ITextEditorLinkAttachment) {
        this.setTextEditorOpenedDialogsData('embed', true);

        this.textEditorInsertEmbedVideoService.openInsertEmbedVideoDialog(attachment, {
            existingLinkAttachments: this.linkAttachments,
            onYesButtonClick: () => {
                this.linkAttachmentsChange.emit(this.linkAttachments);
                this.cdr.markForCheck();
                this.attachmentsListComponent.emitDetectChanges(); // for proper attachments list update
                this.doPropagateChanges();
            },
            onNoButtonClick: () => {
                this.contenteditableComponent.contenteditableElRef.nativeElement.focus();
            },
            afterClosed: () => {
                this.setTextEditorOpenedDialogsData('embed', false);
            },
        });
    }

    /*//#region giphy // todo for futher  giphy support
    onGiphyItemClick($event: ITextEditorLinkAttachment) {
        this.linkAttachments.push($event);
        this.linkAttachmentsChange.emit(this.linkAttachments);
        //this.cdr.markForCheck(); //todo should we emit this
        this.attachmentsListComponent.emitDetectChanges();
        this.doPropagateChanges();
    }
    //#endregion*/

    onInsertLinkAttachment($event) {
        this.onLinkAttachmentsChanged([...this.linkAttachments, $event]);
    }

    onEditLinkAttachment($event: ITextEditorLinkAttachment) {
        if ($event.LinkType === TextEditorAttachmentType.Youtube) this.openInsertEmbedVideoDialog($event);
    }

    onContenteditableClick($event: MouseEvent) {
        // todo move to fi-contenteditable?
        //this.handleMediaClick(e) || this.handleLinkClick($event) || this.handleEmbedClick(e);
        this.handleLinkClick($event);
        this.advancedTextHandlerService.onClick$.next($event);
        this.focusedNodeElement = this.textEditorService.getFocusedElement();
    }

    onFilesPasted($event: File[]) {
        this.fileUpload2Service.silentUploadFiles($event).then((mediaBankFileReferences) => {
            this.onMediaBankFileReferencesChanged(this.mediaBankFileReferences.concat(mediaBankFileReferences));
        });
    }

    private handleLinkClick($event: MouseEvent): boolean {
        const linkElement: HTMLElement = <any>(
            (eventFindInPath($event, 'ccm-text-item-link') || eventFindInPath($event, 'a[ccm-text-item-link]'))
        );
        if (!linkElement) return false;

        $event.preventDefault();
        this.setTextEditorOpenedDialogsData('link', true);
        this.textEditorInsertLinkService
            .insertLink({
                parentElement: linkElement,
                linkToType: undefined,
                params: {
                    model: JSON.parse(linkElement.getAttribute('text-item-model')),
                    title: linkElement.textContent,
                },
            })
            .onYesButtonClick(() => this.contenteditableComponent.triggerChangeEvent())
            .afterClosed(() => this.setTextEditorOpenedDialogsData('link', false));
        return true;
    }

    doValidate() {
        return {
            required: this.required
                ? !(this.ngModel || this.mediaBankFileReferences.length || this.linkAttachments.length)
                : false,
        };
    }

    insertMediaSilentMode = false;
    insertMediaUsingCamera() {
        this.insertMediaSilentMode = true;
        this.uploadHelperComponent.onUseCameraInvoked();
    }
    insertMediaUsingBrowseDevice() {
        this.insertMediaSilentMode = false;
        this.uploadHelperComponent.onBrowseDeviceInvoked();
    }
    onFilesSelected($event: ArrayLike<File>) {
        if (this.insertMediaSilentMode) {
            this.fileUpload2Service.silentUploadFiles($event).then((mediaBankFileReferences) => {
                this.onMediaBankFileReferencesChanged(this.mediaBankFileReferences.concat(mediaBankFileReferences));
            });
        } else {
            this.openInsertMediaDialog($event);
        }
    }
    protected onNewUserTagged(newTaggedUser: ILightWeightUser) {
        if (this.taggedUsers && !this.taggedUsers.find((u) => u.Id === newTaggedUser.Id)) {
            // add new tagged user to array for proper avatar updating after switching from edit to view mode
            this.taggedUsers.push(newTaggedUser);
        }
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
